import {
    Box,
    Button,
    Card,
    CardBody,
    CardHeader,
    Center,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Icon,
    Select,
    Stack,
    Text,
    SimpleGrid
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Roles } from "../../interfaes/user";
import { onGetUsers, getMyProfile } from "../../http/users";
import { FiFileText } from "react-icons/fi";
import ModernLoader from "../../components/ModernLoader";
import SearchableSelect from "../../components/SearchableSelect";

export default function IEPReportFilter() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Filter states
    const [year, setYear] = useState('');
    const [teacherId, setTeacherId] = useState('');

    // Data states
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        const fetchTeachers = async () => {
            try {
                const [usersResponse, profileResponse] = await Promise.all([
                    onGetUsers(),
                    getMyProfile()
                ]);

                const allUsers = usersResponse.data.userResponseList || [];
                const filteredTeachers = allUsers.filter((e) => e?.roles?.includes(Roles.TEACHER));
                setTeachers(filteredTeachers);

                const profile = profileResponse.data;
                setCurrentUser(profile);
                const isTeacher = profile.roles?.includes(Roles.TEACHER) && !profile.roles?.includes(Roles.ADMIN);

                if (isTeacher) {
                    setTeacherId(profile.id);
                }
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        fetchTeachers();
    }, []);

    const handleSearch = () => {
        const params = new URLSearchParams();
        if (year) params.append('year', year);
        if (teacherId) params.append('teacherId', teacherId);

        navigate(`/iep-student?${params.toString()}`);
    };

    const handleCancel = () => {
        setYear('');
        setTeacherId('');
    };

    if (loading) {
        return <ModernLoader h="60vh" message="Initializing report..." />;
    }

    return (
        <Box maxW="600px" mx="auto" py={12}>
            <Card variant="elevated" boxShadow="xl" borderRadius="20px" overflow="hidden">
                <CardHeader bg="#1b73e8" color="white" py={8}>
                    <Flex align="center">
                        <Icon as={FiFileText} mr={3} boxSize={8} />
                        <Heading size="lg">IEP Report Filter</Heading>
                    </Flex>
                    <Text mt={2} opacity={0.9} fontSize="md">Select Teacher and Year to generate report</Text>
                </CardHeader>
                <CardBody p={10}>
                    <Stack spacing={8}>
                        <FormControl isDisabled={currentUser?.roles?.includes(Roles.TEACHER) && !currentUser?.roles?.includes(Roles.ADMIN)}>
                            <FormLabel fontWeight="600">{t('users:TEACHER')}</FormLabel>
                            <SearchableSelect
                                options={teachers.map(t => ({ label: t.displayName, value: t.id }))}
                                value={teacherId}
                                onChange={(e) => setTeacherId(e.target.value)}
                                label={t('users:TEACHER')}
                                isDisabled={currentUser?.roles?.includes(Roles.TEACHER) && !currentUser?.roles?.includes(Roles.ADMIN)}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel fontWeight="600">IEP Year</FormLabel>
                            <SearchableSelect
                                options={['2080', '2081', '2082', '2083', '2084', '2085'].map(a => ({ label: a, value: a }))}
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                                label="IEP Year"
                            />
                        </FormControl>

                        <Divider pt={4} />

                        <Flex gap={4} justify="flex-end">
                            <Button
                                variant="ghost"
                                colorScheme="red"
                                size="lg"
                                px={10}
                                onClick={handleCancel}
                                borderRadius="12px"
                            >
                                {t('studentLocal:manageStudents.cancel') || 'Cancel'}
                            </Button>
                            <Button
                                colorScheme="blue"
                                size="lg"
                                px={12}
                                onClick={handleSearch}
                                borderRadius="12px"
                                boxShadow="md"
                                _hover={{ transform: 'translateY(-1px)', boxShadow: 'lg' }}
                            >
                                {t('studentLocal:search') || 'Search'}
                            </Button>
                        </Flex>
                    </Stack>
                </CardBody>
            </Card>
        </Box>
    );
}
